
import { useState, useEffect } from "react";
import { PageContainer } from "@/components/layout/PageContainer";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { toast } from "@/components/ui/use-toast";
import { useNavigate } from "react-router-dom";
import { 
  auth, 
  signInWithGoogle, 
  registerWithEmail, 
  loginWithEmail, 
  logoutUser,
  getUserSubscriptionData,
  cancelUserSubscription,
  updateUserSubscriptionStatus
} from "@/lib/firebase";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { initializeSubscription } from "@/lib/payment";
import { onAuthStateChanged } from "firebase/auth";
import { format } from "date-fns";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { SuccessCelebration } from "@/components/features/scanner/SuccessCelebration";

const ProfilePage = () => {
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [displayName, setDisplayName] = useState("");
  const [user, setUser] = useState<any>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [showCancelDialog, setShowCancelDialog] = useState(false);
  const [cancelConfirmValue, setCancelConfirmValue] = useState("");
  const [subscription, setSubscription] = useState<{
    subscribed: boolean;
    remainingScans: number;
    subscriptionEndDate: Date | null;
    subscriptionTier: 'basic' | 'pro';
    subscriptionId: string | null;
  }>({
    subscribed: false,
    remainingScans: 0,
    subscriptionEndDate: null,
    subscriptionTier: 'basic',
    subscriptionId: null
  });
  const [showSubscriptionCelebration, setShowSubscriptionCelebration] = useState(false);
  const navigate = useNavigate();
  
  // Check authentication status on component mount
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (currentUser) => {
      setIsLoggedIn(!!currentUser);
      if (currentUser) {
        setUser(currentUser);
        fetchUserSubscription(currentUser.uid);
      } else {
        setUser(null);
      }
    });
    
    return () => unsubscribe();
  }, []);
  
  // On mount, check for subscription success in URL
  useEffect(() => {
    const qp = new URLSearchParams(window.location.search);
    const status = qp.get("subscription");
    const tierParam = qp.get("tier");
    
    if (status === "success" && user) {
      (async () => {
        // Make sure we have a valid tier parameter
        const validTier = tierParam === 'pro' ? 'pro' : 'basic';
        
        console.log(`Processing successful subscription for user ${user.uid}, tier: ${validTier}`);
        await updateUserSubscriptionStatus(user.uid, validTier);
        const subscriptionData = await getUserSubscriptionData(user.uid);
        setSubscription(subscriptionData);
        setShowSubscriptionCelebration(true);
        
        // Clean up URL
        qp.delete("subscription");
        qp.delete("tier");
        window.history.replaceState({}, document.title, window.location.pathname);
      })();
    }
  }, [user]);
  
  const fetchUserSubscription = async (userId: string) => {
    try {
      const subscriptionData = await getUserSubscriptionData(userId);
      console.log("Fetched subscription data:", subscriptionData);
      setSubscription(subscriptionData);
    } catch (error) {
      console.error("Error fetching subscription:", error);
      toast({
        title: "Error",
        description: "Could not load your subscription status. Please try again.",
        variant: "destructive",
      });
    }
  };
  
  const handleRegister = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    
    if (!email || !password || !displayName) {
      toast({
        title: "Registration Failed",
        description: "Please fill out all fields.",
        variant: "destructive",
      });
      setIsLoading(false);
      return;
    }
    
    const user = await registerWithEmail(email, password, displayName);
    if (user) {
      setUser(user);
      setIsLoggedIn(true);
    }
    
    setIsLoading(false);
  };
  
  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    
    if (!email || !password) {
      toast({
        title: "Login Failed",
        description: "Please enter both email and password.",
        variant: "destructive",
      });
      setIsLoading(false);
      return;
    }
    
    const user = await loginWithEmail(email, password);
    if (user) {
      setUser(user);
      setIsLoggedIn(true);
    }
    
    setIsLoading(false);
  };
  
  const handleGoogleLogin = async () => {
    setIsLoading(true);
    const user = await signInWithGoogle();
    if (user) {
      setUser(user);
      setIsLoggedIn(true);
    }
    setIsLoading(false);
  };
  
  const handleLogout = async () => {
    const success = await logoutUser();
    if (success) {
      setIsLoggedIn(false);
      setUser(null);
      navigate("/");
    }
  };
  
  const handleSubscribe = async (tier: 'basic' | 'pro') => {
    setIsLoading(true);
    try {
      console.log(`Initiating ${tier} subscription...`);
      await initializeSubscription(tier);
      // The user will be redirected by Stripe, or an error will be shown by the payment module
    } finally {
      setIsLoading(false);
    }
  };
  
  const handleCancelSubscription = async () => {
    if (cancelConfirmValue.toLowerCase() !== "cancel") {
      toast({
        title: "Cancellation Failed",
        description: "Please type 'cancel' to confirm subscription cancellation.",
        variant: "destructive",
      });
      return;
    }
    
    setIsLoading(true);
    try {
      console.log("Attempting to cancel subscription...");
      const success = await cancelUserSubscription();
      if (success) {
        toast({
          title: "Subscription Cancelled",
          description: "Your subscription has been cancelled. Your remaining scans are preserved.",
        });
        
        // Refresh subscription data
        if (user) {
          fetchUserSubscription(user.uid);
        }
        
        setShowCancelDialog(false);
        setCancelConfirmValue("");
      } else {
        throw new Error("Failed to cancel subscription. Please contact us at +47 481 05 946");
      }
    } catch (error: any) {
      console.error("Error canceling subscription:", error);
      toast({
        title: "Error",
        description: error.message || "Failed to cancel your subscription. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };
  
  if (!isLoggedIn) {
    return (
      <PageContainer>
        <div className="max-w-md mx-auto py-12">
          <Card>
            <CardHeader>
              <CardTitle className="text-center">Welcome to PokeScan</CardTitle>
              <CardDescription className="text-center">
                Sign in or create an account to track your collection and get more card scans!
              </CardDescription>
            </CardHeader>
            <CardContent>
              <Tabs defaultValue="login" className="space-y-4">
                <TabsList className="grid w-full grid-cols-2">
                  <TabsTrigger value="login">Login</TabsTrigger>
                  <TabsTrigger value="register">Register</TabsTrigger>
                </TabsList>
                
                <TabsContent value="login">
                  <form onSubmit={handleLogin} className="space-y-4">
                    <div className="space-y-2">
                      <Label htmlFor="email">Email</Label>
                      <Input 
                        id="email" 
                        type="email" 
                        placeholder="your@email.com" 
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="password">Password</Label>
                      <Input 
                        id="password" 
                        type="password" 
                        placeholder="••••••••" 
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                      />
                    </div>
                    <Button 
                      type="submit" 
                      className="w-full bg-pokemon-blue hover:bg-pokemon-dark-blue"
                      disabled={isLoading}
                    >
                      {isLoading ? "Signing In..." : "Sign In"}
                    </Button>
                  </form>
                </TabsContent>
                
                <TabsContent value="register">
                  <form onSubmit={handleRegister} className="space-y-4">
                    <div className="space-y-2">
                      <Label htmlFor="displayName">Name</Label>
                      <Input 
                        id="displayName" 
                        type="text" 
                        placeholder="Your Name" 
                        value={displayName}
                        onChange={(e) => setDisplayName(e.target.value)}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="reg-email">Email</Label>
                      <Input 
                        id="reg-email" 
                        type="email" 
                        placeholder="your@email.com" 
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="reg-password">Password</Label>
                      <Input 
                        id="reg-password" 
                        type="password" 
                        placeholder="••••••••" 
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                      />
                    </div>
                    <Button 
                      type="submit" 
                      className="w-full bg-pokemon-blue hover:bg-pokemon-dark-blue"
                      disabled={isLoading}
                    >
                      {isLoading ? "Creating Account..." : "Create Account"}
                    </Button>
                  </form>
                </TabsContent>
              </Tabs>
              
              <div className="relative my-6">
                <div className="absolute inset-0 flex items-center">
                  <div className="w-full border-t border-gray-200"></div>
                </div>
                <div className="relative flex justify-center text-sm">
                  <span className="px-2 bg-white text-gray-500">Or continue with</span>
                </div>
              </div>
              
              <Button 
                type="button" 
                variant="outline" 
                className="w-full"
                onClick={handleGoogleLogin}
                disabled={isLoading}
              >
                <svg className="mr-2 h-4 w-4" viewBox="0 0 24 24">
                  <path
                    d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                    fill="#4285F4"
                  />
                  <path
                    d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                    fill="#34A853"
                  />
                  <path
                    d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                    fill="#FBBC05"
                  />
                  <path
                    d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                    fill="#EA4335"
                  />
                </svg>
                Google
              </Button>
            </CardContent>
          </Card>
        </div>
      </PageContainer>
    );
  }
  
  const renderSubscriptionContent = () => {
    if (subscription.subscribed) {
      return (
        <div className="space-y-4">
          <div className="bg-green-100 text-green-800 px-4 py-3 rounded-md flex items-center">
            <div className="flex items-center">
              <svg className="h-5 w-5 mr-2" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
              </svg>
              <div>
                <p className="font-medium">
                  {subscription.subscriptionTier === 'pro' ? 'Pro Subscription Active' : 'Basic Subscription Active'}
                </p>
                {subscription.subscriptionEndDate && (
                  <p className="text-sm text-green-700">
                    Renews on: {format(subscription.subscriptionEndDate, 'PP')}
                  </p>
                )}
              </div>
            </div>
          </div>
          <div className="bg-blue-50 p-4 rounded-md">
            <p className="text-sm text-blue-800">
              {subscription.subscriptionTier === 'pro'
                ? 'Your Pro subscription gives you unlimited scans!'
                : `Your Basic subscription gives you ${subscription.remainingScans} scans this month!`
              }
            </p>
          </div>
        </div>
      );
    } else {
      return (
        <div className="space-y-4">
          <p className="text-sm">
            You have <span className="font-bold">{subscription.remainingScans}</span> scans remaining.
          </p>
          
          <div className="grid grid-cols-2 gap-3">
            <div className="border rounded-md p-3 text-center">
              <h3 className="font-bold text-lg">Basic</h3>
              <p className="text-2xl font-bold text-pokemon-blue">$1.99<span className="text-sm text-gray-500">/mo</span></p>
              <p className="text-sm text-muted-foreground mb-4">30 scans/month</p>
              <Button 
                onClick={() => handleSubscribe('basic')}
                className="w-full bg-pokemon-blue hover:bg-pokemon-blue/90"
                disabled={isLoading}
                size="sm"
              >
                Subscribe
              </Button>
            </div>
            
            <div className="border border-yellow-300 bg-gradient-to-b from-yellow-50 to-amber-50 rounded-md p-3 text-center relative">
              <div className="absolute -top-2 -right-2 bg-pokemon-yellow text-xs text-pokemon-blue px-2 py-0.5 rounded-full font-bold">
                POPULAR
              </div>
              <h3 className="font-bold text-lg">Pro</h3>
              <p className="text-2xl font-bold text-pokemon-blue">$8.99<span className="text-sm text-gray-500">/mo</span></p>
              <p className="text-sm text-muted-foreground mb-4">Unlimited* scans</p>
              <Button 
                onClick={() => handleSubscribe('pro')}
                className="w-full bg-pokemon-yellow text-pokemon-blue hover:bg-pokemon-yellow/90"
                disabled={isLoading}
                size="sm"
              >
                Subscribe
              </Button>
            </div>
          </div>
          
          <p className="text-xs text-center text-muted-foreground mt-2">
            Cancel anytime. No hidden fees.<br />
            <span className="text-[10px] text-gray-400">*"Unlimited" means up to 800 scans per month. See <a href="/terms" className="underline">Terms</a>.</span>
          </p>
        </div>
      );
    }
  };

  return (
    <PageContainer>
      {showSubscriptionCelebration && (
        <SuccessCelebration
          onClose={() => setShowSubscriptionCelebration(false)}
          message={subscription.subscriptionTier === 'pro' 
            ? "You now have unlimited scans with Pro!" 
            : `You now have ${subscription.remainingScans} monthly scans with Basic!`}
          scanCount={subscription.remainingScans}
          subscriptionTier={subscription.subscriptionTier}
        />
      )}
      <div className="max-w-3xl mx-auto">
        <h1 className="text-2xl font-bold mb-6">Your Profile</h1>
        
        <Card className="mb-8">
          <CardContent className="p-6">
            <div className="flex flex-col sm:flex-row items-center gap-6">
              <Avatar className="w-24 h-24">
                <AvatarImage src={user?.photoURL || `https://api.dicebear.com/7.x/avataaars/svg?seed=${user?.email}`} alt="User Avatar" />
                <AvatarFallback>{user?.displayName?.substring(0, 2) || user?.email?.substring(0, 2) || "UK"}</AvatarFallback>
              </Avatar>
              <div className="flex-grow">
                <h2 className="text-xl font-semibold">{user?.displayName || "User"}</h2>
                <p className="text-muted-foreground">{user?.email}</p>
                {subscription.subscribed && (
                  <div className="mt-2 flex items-center gap-2">
                    <div className="bg-green-100 text-green-800 px-2 py-1 rounded-md text-sm font-medium">
                      {subscription.subscriptionTier === 'pro' ? 'Pro Member' : 'Basic Member'}
                    </div>
                    {subscription.subscriptionEndDate && (
                      <span className="text-sm text-muted-foreground">
                        Renews {format(subscription.subscriptionEndDate, 'MMM d, yyyy')}
                      </span>
                    )}
                  </div>
                )}
              </div>
            </div>
          </CardContent>
        </Card>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <Card>
            <CardHeader>
              <CardTitle>Subscription</CardTitle>
              <CardDescription>
                {subscription.subscribed 
                  ? 'Manage your subscription' 
                  : 'Upgrade to get more scans and features'}
              </CardDescription>
            </CardHeader>
            <CardContent>
              {renderSubscriptionContent()}
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Collection Stats</CardTitle>
              <CardDescription>Track your collection progress</CardDescription>
            </CardHeader>
            <CardContent>
              <dl className="space-y-2">
                <div className="flex justify-between">
                  <dt className="text-muted-foreground">Total Cards</dt>
                  <dd className="font-medium">0</dd>
                </div>
                <div className="flex justify-between">
                  <dt className="text-muted-foreground">Collection Value</dt>
                  <dd className="font-medium">$0.00</dd>
                </div>
                <div className="flex justify-between">
                  <dt className="text-muted-foreground">Most Valuable Card</dt>
                  <dd className="font-medium">N/A</dd>
                </div>
                <div className="flex justify-between">
                  <dt className="text-muted-foreground">Sets Collected</dt>
                  <dd className="font-medium">0</dd>
                </div>
              </dl>
            </CardContent>
          </Card>
        </div>

        <Card className="mt-6">
          <CardHeader>
            <CardTitle>Account Settings</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="name">Name</Label>
                <Input id="name" defaultValue={user?.displayName || ""} disabled />
              </div>
              <div className="space-y-2">
                <Label htmlFor="account-email">Email</Label>
                <Input id="account-email" value={user?.email || ""} readOnly />
              </div>
            </div>
            
            <div className="pt-4">
              <Button 
                variant="destructive" 
                className="w-full"
                onClick={handleLogout}
                disabled={isLoading}
              >
                Log Out
              </Button>
            </div>
            {subscription.subscribed && (
              <div className="pt-2">
                <Dialog open={showCancelDialog} onOpenChange={setShowCancelDialog}>
                  <DialogTrigger asChild>
                    <Button variant="outline" size="sm" className="w-full text-red-600 hover:text-red-700 hover:bg-red-50">
                      Cancel Subscription
                    </Button>
                  </DialogTrigger>
                  <DialogContent>
                    <DialogHeader>
                      <DialogTitle>Are you sure you want to cancel?</DialogTitle>
                      <DialogDescription>
                        You'll lose access to these valuable benefits:
                      </DialogDescription>
                    </DialogHeader>
                    <div className="space-y-4 py-3">
                      <div className="flex items-start gap-2">
                        <div className="h-5 w-5 text-red-500 shrink-0">✗</div>
                        <p className="text-sm">
                          {subscription.subscriptionTier === 'pro' 
                            ? 'Unlimited card scans - essential for serious collectors'
                            : 'Monthly card scans - keep growing your collection'}
                        </p>
                      </div>
                      <div className="flex items-start gap-2">
                        <div className="h-5 w-5 text-red-500 shrink-0">✗</div>
                        <p className="text-sm">
                          Accurate price tracking - don't miss out on cards increasing in value
                        </p>
                      </div>
                      <div className="flex items-start gap-2">
                        <div className="h-5 w-5 text-red-500 shrink-0">✗</div>
                        <p className="text-sm">
                          You scan count will be reset to 0 immediately
                        </p>
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="cancelConfirm">Type "cancel" to confirm</Label>
                      <Input
                        id="cancelConfirm"
                        value={cancelConfirmValue}
                        onChange={(e) => setCancelConfirmValue(e.target.value)}
                        placeholder="Type 'cancel' to confirm"
                      />
                    </div>
                    <DialogFooter>
                      <Button
                        variant="outline"
                        onClick={() => setShowCancelDialog(false)}
                      >
                        Keep Subscription
                      </Button>
                      <Button
                        variant="destructive"
                        onClick={handleCancelSubscription}
                        disabled={cancelConfirmValue.toLowerCase() !== 'cancel' || isLoading}
                      >
                        {isLoading ? "Cancelling..." : "Cancel Subscription"}
                      </Button>
                    </DialogFooter>
                  </DialogContent>
                </Dialog>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </PageContainer>
  );
};

export default ProfilePage;
